import { AxisCode } from '@atlas/types'

/**
 * An axis value code read as words, e.g. `companion_animal` → `companion animal`.
 *
 * A display fallback only: a seeded `axis_value` carries its own label, so this is
 * for the codes that reach the UI without one (a business unit column, a tag whose
 * label the read model did not resolve). Casing is left to the caller's styling.
 */
export const humanizeCode = (code: string): string => code.replace(/_/g, ' ')

/**
 * An axis's name as a picker or filter shows it. The brand axis holds brands as roots
 * and their products as children, so it reads `Brand / Product` wherever its whole
 * value list is offered; the registry keeps its own name for the admin screens.
 */
export const axisDisplayName = (axis: { code: string; name: string }): string =>
	axis.code === AxisCode.BRAND ? 'Brand / Product' : axis.name

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/**
 * How long ago an ISO timestamp was, in words — "3 hours ago", "yesterday".
 *
 * Past-only by design: every caller renders a stored `updatedAt`/`createdAt`, and a
 * clock skew of a few seconds should read as "just now" rather than as the future.
 * Falls back to a locale date beyond a month, where "5 weeks ago" is less useful
 * than the date itself.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
	const then = new Date(iso).getTime()
	if (Number.isNaN(then)) return ''
	const elapsed = Math.max(0, now - then)

	if (elapsed < MINUTE_MS) return 'just now'
	if (elapsed < HOUR_MS) {
		const minutes = Math.floor(elapsed / MINUTE_MS)
		return `${minutes} min ago`
	}
	if (elapsed < DAY_MS) {
		const hours = Math.floor(elapsed / HOUR_MS)
		return `${hours} hour${hours === 1 ? '' : 's'} ago`
	}
	const days = Math.floor(elapsed / DAY_MS)
	if (days === 1) return 'yesterday'
	if (days < 30) return `${days} days ago`
	return new Date(iso).toLocaleDateString()
}

/** Up to two initials from a display name, e.g. "Júlia Nunes" → "JN". */
export function initials(name: string): string {
	return name
		.split(/\s+/)
		.map((part) => part[0])
		.slice(0, 2)
		.join('')
		.toUpperCase()
}
